import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, SimpleGrid, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import {
  IconBuildingStore,
  IconCalculator,
  IconLayoutDashboard,
  IconPackage,
  IconSettings,
  IconToolsKitchen2,
  IconUsers,
  type Icon,
} from '@tabler/icons-react';
import {
  navSections,
  settingsItem,
  type NavItem,
  type NavSection,
} from '../components/AppShell/navItems';
import { GlobalSearch } from '../components/GlobalSearch/GlobalSearch';

// The "Desktop" — an app-launcher hub inspired by ERPNext's desk: a grid of
// module tiles; tapping one opens a popover of its sub-items (which are our
// existing pages). The "overview" (عام) section is flattened onto the main
// grid — its items sit as direct tiles alongside the module tiles instead of
// hiding behind a single عام tile. Wheels strengths kept: Arabic-first RTL,
// brand palette, the same Ctrl+K search reused as a hero. Data comes straight
// from navSections so the hub and the sidebar never drift.

const OVERVIEW_KEY = 'nav.sections.overview';

const MODULE_META: Record<string, { icon: Icon; color: string }> = {
  'nav.sections.overview': { icon: IconLayoutDashboard, color: 'indigo' },
  'nav.sections.catalog': { icon: IconToolsKitchen2, color: 'grape' },
  'nav.sections.operations': { icon: IconPackage, color: 'teal' },
  'nav.sections.accounting': { icon: IconCalculator, color: 'green' },
  'nav.sections.hr': { icon: IconUsers, color: 'orange' },
  'nav.sections.organization': { icon: IconBuildingStore, color: 'blue' },
};

// A synthetic "System" module so Settings is reachable from the hub too.
const SYSTEM_SECTION: NavSection = {
  titleKey: 'nav.sections.system',
  items: [settingsItem],
};
const SYSTEM_META = { icon: IconSettings, color: 'gray' } as const;

export function DesktopHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState<NavSection | null>(null);

  const metaFor = (key: string) => MODULE_META[key] ?? SYSTEM_META;

  // Overview (عام) items are hoisted to the top level as direct tiles; every
  // other section stays a module tile that opens its items in a modal.
  const overviewItems: NavItem[] =
    navSections.find((s) => s.titleKey === OVERVIEW_KEY)?.items ?? [];
  const overviewColor = metaFor(OVERVIEW_KEY).color;
  const moduleSections: NavSection[] = [
    ...navSections.filter((s) => s.titleKey !== OVERVIEW_KEY),
    SYSTEM_SECTION,
  ];

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10">
      <Stack gap={6} align="center" mb="xl">
        <Text size="28px" fw={800} c="var(--wheels-ink-strong)">
          {t('desktop.title')}
        </Text>
        <Text size="sm" c="dimmed">
          {t('desktop.subtitle')}
        </Text>
      </Stack>

      <div className="mx-auto mb-10 max-w-2xl">
        <GlobalSearch variant="hero" />
      </div>

      <SimpleGrid cols={{ base: 3, xs: 4, sm: 5, md: 6 }} spacing="lg" verticalSpacing="xl">
        {overviewItems.map((item) => {
          const ItemIcon = item.icon;
          return (
            <UnstyledButton
              key={item.to}
              onClick={() => navigate(item.to)}
              className="flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-transform hover:-translate-y-0.5"
              aria-label={t(item.labelKey)}
            >
              <ThemeIcon size={64} radius="lg" variant="light" color={overviewColor}>
                <ItemIcon size={30} stroke={1.6} />
              </ThemeIcon>
              <Text size="sm" fw={600} c="var(--wheels-ink-strong)" lineClamp={2}>
                {t(item.labelKey)}
              </Text>
            </UnstyledButton>
          );
        })}
        {moduleSections.map((section) => {
          const meta = metaFor(section.titleKey);
          const ModIcon = meta.icon;
          return (
            <UnstyledButton
              key={section.titleKey}
              onClick={() => setOpenSection(section)}
              className="flex flex-col items-center gap-2 rounded-xl p-3 text-center transition-transform hover:-translate-y-0.5"
              aria-label={t(section.titleKey)}
            >
              <ThemeIcon size={64} radius="lg" variant="light" color={meta.color}>
                <ModIcon size={30} stroke={1.6} />
              </ThemeIcon>
              <Text size="sm" fw={600} c="var(--wheels-ink-strong)" lineClamp={2}>
                {t(section.titleKey)}
              </Text>
            </UnstyledButton>
          );
        })}
      </SimpleGrid>

      <Modal
        opened={openSection !== null}
        onClose={() => setOpenSection(null)}
        centered
        size="lg"
        title={
          openSection ? (
            <Text fw={700} size="lg" c="var(--wheels-ink-strong)">
              {t(openSection.titleKey)}
            </Text>
          ) : null
        }
      >
        {openSection && (
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="md" verticalSpacing="lg" pb="sm">
            {openSection.items.map((item) => {
              const ItemIcon = item.icon;
              const meta = metaFor(openSection.titleKey);
              return (
                <UnstyledButton
                  key={item.to}
                  onClick={() => {
                    setOpenSection(null);
                    navigate(item.to);
                  }}
                  className="flex flex-col items-center gap-2 rounded-lg p-2 text-center hover:bg-[var(--wheels-bg)] transition-colors"
                >
                  <ThemeIcon size={48} radius="md" variant="light" color={meta.color}>
                    <ItemIcon size={22} stroke={1.6} />
                  </ThemeIcon>
                  <Text size="xs" fw={500} c="var(--wheels-ink)" lineClamp={2}>
                    {t(item.labelKey)}
                  </Text>
                </UnstyledButton>
              );
            })}
          </SimpleGrid>
        )}
      </Modal>
    </div>
  );
}
